import { useEffect, useState } from 'react'
import { Play, X } from 'lucide-react'
import { Button, FormError, Pager, WORKER_ROLE_LABELS, type WorkerRole } from '../../../core/index.js'
import type { Posting, ScheduleBoardView } from '../../types.js'
import { useDemoSeat } from '../../hooks.js'

const AHEAD_TEXT: Record<Posting['ahead'][number]['stage'], string> = { posted: 'Posted', urgent: 'Urgent: everyone told', bridge_callout: 'Bridge callout opens', critical: 'Lock: Needs you', started: 'Starts' }
/** What each move ahead will do and why, in the same voice as the history's account (seat life reasons plan section 7). */
const AHEAD_WHY: Record<Posting['ahead'][number]['stage'], string> = {
  posted: 'the seat goes into the daily digest of everyone eligible; first to accept gets it',
  urgent: 'everyone eligible is told at once and the race runs to the start',
  bridge_callout: 'Clinical Bridge is on and the seat is still open, so the engine opens a callout for it',
  critical: 'the engine’s automatic moves are spent; the seat is under Needs you, nobody assigns it by hand',
  started: 'still open at the start, the seat closes as ended unfilled and is logged',
}
const DONE_TEXT: Record<Posting['history'][number]['kind'], string> = { born: 'Born', posted: 'Posted', nudged: 'Nudged: in the digest again', offered: 'Offered', offer_declined: 'Offer declined', offer_expired: 'Offer ran out', blasted: 'Urgent: everyone told', repeated: 'Told again', declined: 'Someone can’t', callout_opened: 'Bridge callout opened', locked: 'Locked: under Needs you', filled: 'Filled', ran_short: 'Ran short', withdrawn: 'Taken down', ended_unfilled: 'Started: ended unfilled' }
const ROLES = Object.keys(WORKER_ROLE_LABELS) as WorkerRole[]

/** "in 1:23" or "now". */
function countdown(iso: string, now: number): string {
  const seconds = Math.max(0, Math.round((Date.parse(iso) - now) / 1000))
  return seconds === 0 ? 'now' : `in ${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`
}

const clock = (iso: string): string => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', second: '2-digit' })

/** The demo seat's timeline as a stepper: what happened, ticked, and what the engine does next, counting down. */
function DemoStepper({ posting }: { posting: Posting }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000)
    return () => window.clearInterval(timer)
  }, [])
  const ended = posting.status !== 'open'
  return (
    <ol className="mt-1 flex flex-col gap-0.5 text-[11px]">
      {posting.history.map((entry, index) => (
        <li key={`${entry.kind}-${entry.at}-${index}`} className="flex items-start gap-2 text-ink">
          <span className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full bg-green" aria-hidden="true" />
          <span className="w-20 shrink-0 tabular-nums text-ink-mid">{clock(entry.at)}</span>
          <span className="min-w-0">
            <span className="font-bold">
              {DONE_TEXT[entry.kind]}
              {'told' in entry && entry.told !== undefined ? ` (told ${entry.told})` : ''}
            </span>
            {/* The engine's account of the move, in its own words from the API (seat life reasons plan D1). */}
            {entry.why && <span className="block text-ink-mid">{entry.why}</span>}
          </span>
        </li>
      ))}
      {!ended &&
        posting.ahead.map((move) => (
          <li key={`${move.stage}-${move.at}`} className="flex items-start gap-2 text-ink-mid">
            <span className="mt-1 inline-block h-2 w-2 shrink-0 rounded-full border border-marine/40" aria-hidden="true" />
            <span className="w-20 shrink-0 tabular-nums">{clock(move.at)}</span>
            <span className="min-w-0">
              <span className="font-bold text-ink">{AHEAD_TEXT[move.stage]}</span> <span className="font-bold text-marine">{countdown(move.at, now)}</span>
              <span className="block">{AHEAD_WHY[move.stage]}</span>
            </span>
          </li>
        ))}
      {ended && <li className="mt-0.5 text-[11px] font-bold text-ink">{posting.status === 'filled' ? `Filled by ${posting.filledBy} - the cycle ended in a fill.` : posting.withdrawn ? 'Taken down.' : 'The shift started with nobody in the seat: ended unfilled, logged.'}</li>}
    </ol>
  )
}

/**
 * The two-minute demo (open shift lifecycle plan section 11.3; demo clients only): pick a unit and role, press Play,
 * and a seat is born whose whole cycle runs in two and a half minutes - posted, the digest, urgent, the repeats, the
 * engine's Bridge callout, the lock, the start - while every other seat keeps the facility's clock. The seat's
 * timeline is a stepper counting down each stage; a worker opened with View as may take it at any stage.
 */
export function DemoPlay({ facilityId, period, units, board }: { facilityId: string; period: string; units: readonly { id: string; name: string }[]; board: ScheduleBoardView | null }) {
  const demo = useDemoSeat(facilityId, period)
  const [unitId, setUnitId] = useState(units[0]?.id ?? '')
  const [role, setRole] = useState<WorkerRole>('CNA')
  const chosenUnit = units.some((unit) => unit.id === unitId) ? unitId : (units[0]?.id ?? '')
  // Every play, newest first: the latest is shown and the rest sit behind a pager, one at a time (the user's call, 28 October 2026).
  const seats = (board?.postings ?? []).filter((posting) => posting.bornOf === 'demo').sort((a, b) => b.postedAt.localeCompare(a.postedAt))
  const live = seats.some((seat) => seat.status === 'open')
  const [page, setPage] = useState(0)
  const newest = seats[0]?.postingId ?? null
  useEffect(() => {
    // A new play is what the presenter is watching: back to the latest whenever one is born.
    setPage(0)
  }, [newest])
  const current = Math.min(page, Math.max(0, seats.length - 1))
  const seat = seats[current]
  return (
    <div className="rounded border border-dashed border-marine/40 bg-marine/5 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-bold uppercase tracking-wide text-marine">Play the lifecycle</span>
        <span className="text-[11px] text-ink-mid">a seat whose whole cycle runs in two and a half minutes, on a demo client only</span>
        <select aria-label="Unit" className="rounded border border-marine/30 bg-white px-2 py-1 text-xs font-bold text-ink" value={chosenUnit} onChange={(event) => setUnitId(event.target.value)}>
          {units.map((unit) => (
            <option key={unit.id} value={unit.id}>
              {unit.name}
            </option>
          ))}
          {units.length === 0 && <option value="">No units listed</option>}
        </select>
        <select aria-label="Role" className="rounded border border-marine/30 bg-white px-2 py-1 text-xs font-bold text-ink" value={role} onChange={(event) => setRole(event.target.value as WorkerRole)}>
          {ROLES.map((key) => (
            <option key={key} value={key}>
              {WORKER_ROLE_LABELS[key]}
            </option>
          ))}
        </select>
        <Button icon={Play} size="sm" busy={demo.play.isPending} disabled={live || chosenUnit === ''} onClick={() => demo.play.mutate({ unitId: chosenUnit, role })}>
          {demo.play.isPending ? 'Playing…' : live ? 'A seat is playing' : 'Play'}
        </Button>
      </div>
      <FormError error={demo.play.error ?? demo.remove.error} fallback="The demo seat could not be played. Is the month released?" />
      {seat && (
        <div key={seat.postingId} className="mt-2 border-t border-marine/15 pt-2">
          <div className="flex flex-wrap items-center gap-2 text-xs text-ink">
            <span className="font-bold">
              {seat.shift.date} {seat.shift.startTime}–{seat.shift.endTime} · {units.find((unit) => unit.id === seat.shift.unitId)?.name ?? seat.shift.unitId} · {seat.shift.role}
            </span>
            <span className="rounded-full bg-marine-lt px-2 py-0.5 text-[10px] font-bold text-marine-dk">{seat.stage}</span>
            {seat.status === 'open' && (
              <Button icon={X} variant="secondary" size="sm" busy={demo.remove.isPending} onClick={() => demo.remove.mutate(seat.postingId)}>
                Take it down
              </Button>
            )}
            {seats.length > 1 && <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-ink-mid">{current === 0 ? 'Latest play' : `${current} ${current === 1 ? 'play' : 'plays'} after this one`}</span>}
          </div>
          <DemoStepper posting={seat} />
          {seats.length > 1 && (
            // One play a page, newest first: the shared pager, as Open shifts in flight has it, so it reads as pagination.
            <div className="mt-2 flex flex-wrap items-center justify-between gap-2 border-t border-marine/15 pt-2">
              <span className="text-[11px] text-ink-mid">
                <span className="font-bold text-ink">{seats.length} plays</span> · newest first, one at a time
              </span>
              <Pager total={seats.length} pageSize={1} page={current} onPage={setPage} label="plays" />
            </div>
          )}
        </div>
      )}
    </div>
  )
}
